import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Sparkle } from "@phosphor-icons/react";

function displayText(value) {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    return String(value.text || value.reason || value.basis || "").trim();
  }
  return "";
}

export default function ScoreInsight({ label, reason, description, directionNote }) {
  const reasonText = displayText(reason);
  const fallbackText = displayText(description);
  const directionText = displayText(directionNote);
  const content = reasonText || fallbackText || directionText;
  const isPersonalized = Boolean(reasonText);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    arrowLeft: 18,
    side: "bottom",
    ready: false,
  });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipId = useId();

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip || typeof window === "undefined") return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 12;
    const gap = 10;

    const fitsBelow = triggerRect.bottom + gap + tooltipRect.height <= viewportHeight - margin;
    const fitsAbove = triggerRect.top - gap - tooltipRect.height >= margin;
    const side = fitsBelow || !fitsAbove ? "bottom" : "top";

    let top = side === "bottom"
      ? triggerRect.bottom + gap
      : triggerRect.top - tooltipRect.height - gap;
    let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);

    top = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));

    const triggerCenter = triggerRect.left + (triggerRect.width / 2);
    const arrowLeft = Math.max(14, Math.min(triggerCenter - left, tooltipRect.width - 14));

    setPosition({ top, left, arrowLeft, side, ready: true });
  }, []);

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    setPosition((current) => ({ ...current, ready: false }));
    const frame = window.requestAnimationFrame(updatePosition);
    return () => window.cancelAnimationFrame(frame);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    const close = () => setOpen(false);
    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      close();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.visualViewport?.addEventListener("resize", close);
    window.visualViewport?.addEventListener("scroll", close);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.visualViewport?.removeEventListener("resize", close);
      window.visualViewport?.removeEventListener("scroll", close);
    };
  }, [open]);

  if (!content) return null;

  const tooltip = open && typeof document !== "undefined"
    ? createPortal(
        <div
          id={tooltipId}
          ref={tooltipRef}
          className={`metric-help__tooltip${isPersonalized ? " metric-help__tooltip--insight" : ""}${position.ready ? " is-positioned" : ""}`}
          role="tooltip"
          data-side={position.side}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            "--metric-help-arrow-left": `${position.arrowLeft}px`,
          }}
        >
          {isPersonalized ? (
            <>
              <span className="metric-help__eyebrow"><Sparkle size={12} weight="fill" /> Why this score</span>
              <span className="metric-help__copy">{reasonText}</span>
              {directionText ? <span className="metric-help__direction">{directionText}</span> : null}
            </>
          ) : (
            <>
              {fallbackText ? <span className="metric-help__copy">{fallbackText}</span> : null}
              {directionText ? <span className="metric-help__direction">{directionText}</span> : null}
            </>
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`metric-help${isPersonalized ? " metric-help--insight" : ""}`}
        aria-label={isPersonalized
          ? `${label}: Why this score. ${reasonText}${directionText ? ` ${directionText}` : ""}`
          : `${label}: ${fallbackText}${directionText ? ` ${directionText}` : ""}`}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") setOpen(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") setOpen(false);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
      >
        {isPersonalized ? (
          <><Sparkle size={11} weight="fill" aria-hidden="true" /><span>Why?</span></>
        ) : (
          <Info size={11} weight="bold" aria-hidden="true" />
        )}
      </button>
      {tooltip}
    </>
  );
}
